import { HomeCalendarPreferencesData, UserPreferencesData } from '../../../models';
import { Presentation } from './homeTypes';

export type AppointmentToolTipField = 'time' | 'client' | 'type' | 'professional' | 'room' | 'notes' | 'payment' | 'pack' | 'confirmation';

export const appointmentToolTipFieldsDefault: AppointmentToolTipField[] = ['time', 'client', 'type', 'professional', 'room', 'pack'];
export const appointmentToolTipFieldsAll: AppointmentToolTipField[] = ['time', 'client', 'type', 'professional', 'room', 'confirmation', 'notes', 'payment', 'pack'];

export const slotGranularityOptions = [15, 20, 30, 60] as const;
export type SlotGranularity = typeof slotGranularityOptions[number];

export const columnWidthOptions = [80, 140, 180, 220, 260] as const;
export type ColumnWidth = typeof columnWidthOptions[number];

export interface CalendarSettings {
  tooltipFields: Record<AppointmentToolTipField, boolean>;
  colors: 'professional' | 'service' | 'auto';
  slotGranularity: SlotGranularity;
  columnWidthByView: Record<Presentation, ColumnWidth>;
}

export interface HomeCalendarPreferenceSource {
  columnType?: Presentation;
  tooltipFields?: string[];
  colors?: 'professional' | 'service' | 'auto';
  slotGranularity?: number;
  columnWidth?: number;
  columnWidthByView?: Partial<Record<Presentation, number>>;
  roomColumnOrder?: string[];
  professionalColumnOrder?: string[];
}

export interface HomeCalendarState {
  columnType: Presentation;
  calendarSettings: CalendarSettings;
  roomColumnOrder: string[];
  professionalColumnOrder: string[];
}

export const shouldUseBranchHomeCalendarPreferences = (
  selectedBranch: string | undefined,
  branchCount: number,
): boolean => !!selectedBranch && branchCount > 1;

export const homeCalendarPreferenceCookieKeys = [
  'HOME_COLUMN_TYPE',
  'APPOINTMENTS_CALENDAR_TOOLTIP_FIELDS',
  'APPOINTMENTS_CALENDAR_COLORS',
  'APPOINTMENTS_CALENDAR_GRANULARITY',
  'APPOINTMENTS_CALENDAR_COLUMN_WIDTH',
  'HOME_COLUMNS_ROOMS',
  'HOME_COLUMNS_PROFESSIONALS',
] as const;

const homeCalendarColumnTypeOptions = ['room', 'professional', 'week', 'day'] as const;

const normalizePresentation = (value?: string): Presentation =>
  homeCalendarColumnTypeOptions.includes(value as Presentation) ? value as Presentation : 'day';

const normalizeCalendarColors = (value?: string): 'professional' | 'service' | 'auto' =>
  value === 'professional' || value === 'service' ? value : 'auto';

const normalizeTooltipFields = (fields?: string[]): Record<AppointmentToolTipField, boolean> => {
  const selectedFields = fields === undefined
    ? new Set(appointmentToolTipFieldsDefault)
    : new Set(fields.filter((f): f is AppointmentToolTipField => appointmentToolTipFieldsAll.includes(f as AppointmentToolTipField)));

  return Object.fromEntries(appointmentToolTipFieldsAll.map(field => [field, selectedFields.has(field)])) as Record<AppointmentToolTipField, boolean>;
};

const normalizeSlotGranularity = (value?: number): SlotGranularity =>
  (slotGranularityOptions as ReadonlyArray<number>).includes(Number(value)) ? Number(value) as SlotGranularity : 60;

const normalizeColumnWidth = (value?: number): ColumnWidth =>
  (columnWidthOptions as ReadonlyArray<number>).includes(Number(value)) ? Number(value) as ColumnWidth : 140;

const allPresentations: Presentation[] = ['day', 'week', 'room', 'professional'];

const normalizeColumnWidthByView = (
  byView?: Partial<Record<Presentation, number>>,
  legacyWidth?: number,
): Record<Presentation, ColumnWidth> => {
  const fallback = normalizeColumnWidth(legacyWidth);
  return Object.fromEntries(
    allPresentations.map(view => [view, normalizeColumnWidth(byView?.[view] ?? legacyWidth) ?? fallback]),
  ) as Record<Presentation, ColumnWidth>;
};

export const buildColumnOrder = (storedOrder: string[] | undefined, availableColumns: string[]): string[] => [
  ...new Set([...(storedOrder ?? []).filter(name => availableColumns.includes(name)), ...availableColumns]),
];

export const areStringArraysEqual = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((value, index) => value === right[index]);

export const reconcileColumnOrder = (
  currentOrder: string[],
  storedOrder: string[] | undefined,
  availableColumns: string[],
): string[] => {
  if (!currentOrder.length) {
    return buildColumnOrder(storedOrder, availableColumns);
  }

  if (!storedOrder?.length) {
    return buildColumnOrder(currentOrder, availableColumns);
  }

  const currentMatchesStoredProjection = areStringArraysEqual(
    currentOrder,
    buildColumnOrder(storedOrder, currentOrder),
  );

  return buildColumnOrder(currentMatchesStoredProjection ? storedOrder : currentOrder, availableColumns);
};

export const hasHomeCalendarPreferenceValues = (source: HomeCalendarPreferenceSource | undefined | null): boolean =>
  !!source && Object.values(source).some(value =>
    value !== undefined &&
    value !== null &&
    value !== '' &&
    !(Array.isArray(value) && value.length === 0),
  );

export const buildHomeCalendarState = (
  source: HomeCalendarPreferenceSource | undefined,
  roomsSet: string[],
  professionalsSet: string[],
): HomeCalendarState => ({
  columnType: normalizePresentation(source?.columnType),
  calendarSettings: {
    tooltipFields: normalizeTooltipFields(source?.tooltipFields),
    colors: normalizeCalendarColors(source?.colors),
    slotGranularity: normalizeSlotGranularity(source?.slotGranularity),
    columnWidthByView: normalizeColumnWidthByView(source?.columnWidthByView, source?.columnWidth),
  },
  roomColumnOrder: buildColumnOrder(source?.roomColumnOrder, roomsSet),
  professionalColumnOrder: buildColumnOrder(source?.professionalColumnOrder, professionalsSet),
});

export const getLegacyHomeCalendarPreferences = (cookies: Record<string, unknown>): HomeCalendarPreferenceSource => ({
  columnType: typeof cookies['HOME_COLUMN_TYPE'] === 'string' ? cookies['HOME_COLUMN_TYPE'] as Presentation : undefined,
  tooltipFields: typeof cookies['APPOINTMENTS_CALENDAR_TOOLTIP_FIELDS'] === 'string'
    ? cookies['APPOINTMENTS_CALENDAR_TOOLTIP_FIELDS'].split(',')
    : undefined,
  colors: typeof cookies['APPOINTMENTS_CALENDAR_COLORS'] === 'string'
    ? cookies['APPOINTMENTS_CALENDAR_COLORS'] as 'professional' | 'service' | 'auto'
    : undefined,
  slotGranularity: undefined,
  columnWidth: undefined,
  roomColumnOrder: typeof cookies['HOME_COLUMNS_ROOMS'] === 'string'
    ? cookies['HOME_COLUMNS_ROOMS'].split(';')
    : undefined,
  professionalColumnOrder: typeof cookies['HOME_COLUMNS_PROFESSIONALS'] === 'string'
    ? cookies['HOME_COLUMNS_PROFESSIONALS'].split(';')
    : undefined,
});

export const getSavedHomeCalendarPreferences = (
  preferences: UserPreferencesData | undefined | null,
  selectedBranch: string | undefined,
  branchCount: number,
): HomeCalendarPreferenceSource | undefined => {
  if (shouldUseBranchHomeCalendarPreferences(selectedBranch, branchCount)) {
    const branchPreferences = selectedBranch ? preferences?.homeCalendarByBranch?.[selectedBranch] : undefined;
    return hasHomeCalendarPreferenceValues(branchPreferences)
      ? branchPreferences
      : preferences?.homeCalendar;
  }

  return preferences?.homeCalendar;
};

export const toLegacyHomeCalendarCookies = (state: HomeCalendarState): Record<(typeof homeCalendarPreferenceCookieKeys)[number], string> => ({
  HOME_COLUMN_TYPE: state.columnType,
  APPOINTMENTS_CALENDAR_TOOLTIP_FIELDS: Object.entries(state.calendarSettings.tooltipFields)
    .filter(([, enabled]) => enabled)
    .map(([field]) => field)
    .join(','),
  APPOINTMENTS_CALENDAR_COLORS: state.calendarSettings.colors,
  APPOINTMENTS_CALENDAR_GRANULARITY: String(state.calendarSettings.slotGranularity),
  APPOINTMENTS_CALENDAR_COLUMN_WIDTH: String(state.calendarSettings.columnWidthByView[state.columnType]),
  HOME_COLUMNS_ROOMS: state.roomColumnOrder.join(';'),
  HOME_COLUMNS_PROFESSIONALS: state.professionalColumnOrder.join(';'),
});

const toHomeCalendarPreferenceData = (state: HomeCalendarState): HomeCalendarPreferencesData => ({
  columnType: state.columnType,
  tooltipFields: Object.entries(state.calendarSettings.tooltipFields)
    .filter(([, enabled]) => enabled)
    .map(([field]) => field),
  colors: state.calendarSettings.colors,
  slotGranularity: state.calendarSettings.slotGranularity,
  columnWidthByView: state.calendarSettings.columnWidthByView,
  roomColumnOrder: state.roomColumnOrder,
  professionalColumnOrder: state.professionalColumnOrder,
});

export const toHomeCalendarPreferences = (
  state: HomeCalendarState,
  currentPreferences?: UserPreferencesData,
  selectedBranch?: string,
  branchCount: number = 0,
): UserPreferencesData => {
  const nextHomeCalendar = toHomeCalendarPreferenceData(state);

  if (shouldUseBranchHomeCalendarPreferences(selectedBranch, branchCount) && selectedBranch) {
    return {
      ...currentPreferences,
      homeCalendar: currentPreferences?.homeCalendar ?? nextHomeCalendar,
      homeCalendarByBranch: {
        ...(currentPreferences?.homeCalendarByBranch ?? {}),
        [selectedBranch]: nextHomeCalendar,
      },
    };
  }

  return {
    ...currentPreferences,
    homeCalendar: nextHomeCalendar,
    homeCalendarByBranch: currentPreferences?.homeCalendarByBranch,
  };
};

export const hasLegacyHomeCalendarCookies = (cookies: Record<string, unknown>): boolean =>
  homeCalendarPreferenceCookieKeys.some(key => typeof cookies[key] === 'string' && (cookies[key] as string).length > 0);
